// src\components\VideoEngine\Templates\MSN_Landscape\Sequences\RankingSlide.js

// This is the workhorse template. It uses a 42% Sidebar  to house the text while the image 
// takes up the rest. It includes:
//  - RankBadge: A circular pop-in element.
//  - Sheen: A luxury light-sweep effect on the sidebar.


// src/components/VideoEngine/Templates/MSN_Landscape/Sequences/RankingSlide.js

import React from "react";
import {
  AbsoluteFill,
  Sequence,
  Audio,
  useCurrentFrame,
  interpolate,
  spring,
  useVideoConfig,
  Img,
} from "remotion";
import { SmartBackground } from "../../../Common/SmartBackground";
import { KaraokeCaptions } from "../../../Common/KaraokeCaptions";

// --- SUB-COMPONENT: Rank Badge ---
const RankBadge = ({ rank, theme, delay }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const isNumeric = /^\d+$/.test(rank);

  const scale = spring({
    frame: frame - delay,
    fps,
    config: { damping: 12, stiffness: 200 },
  });

  // Tactile Rotation (The "Stamp" Effect)
  const rotation = interpolate(scale, [0, 1], [-20, 0]);

  return (
    <div
      style={{
        position: "absolute",
        top: 60,
        left: 60,
        zIndex: 5,
        transform: `scale(${scale}) rotate(${rotation}deg)`,
        transformOrigin: "center center",
        background: theme.primary,
        color: "white",
        boxShadow: "0 10px 25px rgba(0,0,0,0.3)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: theme.fontDisplay || theme.fontMain,
        fontWeight: 900,
        ...(isNumeric
          ? {
            width: 100,
            height: 100,
            borderRadius: "50%",
            fontSize: 48,
          }
          : {
            width: "auto",
            height: "auto",
            minHeight: 60,
            padding: "15px 30px",
            borderRadius: "15px",
            fontSize: 28,
            whiteSpace: "nowrap",
          }),
      }}
    >
      <span style={{ marginTop: 2 }}>
        {isNumeric ? `#${rank}` : rank.toUpperCase()}
      </span>
    </div>
  );
};

// --- MAIN COMPONENT ---
export const RankingSlide = ({
  item,
  theme,
  brand,
  duration,
  ENABLE_AUDIO = true,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 1. CAPTURE THE DELAY
  const audioOffset = item.audioDelay || 0;

  // 2. UPDATE SPRINGS (SEQUENCED)
  const sidebarEnter = spring({
    frame: frame - audioOffset,
    fps,
    config: { damping: 20, stiffness: 100 }
  });

  // REMOVED `rankEnter` from here. We will let the RankBadge child component control its own destiny.

  const metricEnter = spring({
    frame: frame - audioOffset - 10,
    fps,
    config: { damping: 20, stiffness: 100 }, // <--- CRITICALLY DAMPED (No bounce)
  });

  const nameEnter = spring({
    frame: frame - audioOffset - 15,
    fps,
    config: { damping: 20, stiffness: 100 }  // <--- CRITICALLY DAMPED
  });

  const hookEnter = spring({
    frame: frame - audioOffset - 20,
    fps,
    config: { damping: 20, stiffness: 100 } // <--- CRITICALLY DAMPED
  });

  // ANIMATIONS
  const sidebarX = interpolate(sidebarEnter, [0, 1], [-100, 0]);
  const subjectPush = interpolate(sidebarEnter, [0, 1], [0, 22]);
  const sheenPos = interpolate(frame, [0, duration], [0, 200]);

  // The Watermark Fade ---
  const watermarkOpacity = interpolate(
    frame - audioOffset,
    [0, 30],     // Over the first 30 active frames...
    [0, 0.6],    // Fade from 0% to a subtle 60% opacity
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  return (
    <AbsoluteFill style={{ backgroundColor: "#000", overflow: "hidden" }}>
      {ENABLE_AUDIO && item.props?.audioUrl && (
        <Sequence from={audioOffset}>
          <Audio src={item.props.audioUrl} />
        </Sequence>
      )}

      {/* 1. VISUAL LAYER (Pushed by Sidebar) */}
      <AbsoluteFill style={{ transform: `translateX(${subjectPush}%)` }}>
        <SmartBackground src={item.image || ""} duration={duration} />
      </AbsoluteFill>

      {/* 2. EDITORIAL SIDEBAR (The "Magazine" Spine) */}
      <AbsoluteFill style={{
        width: "42%",
        height: "100%",
        backgroundColor: theme.accent,
        clipPath: "polygon(0 0, 100% 0, 90% 100%, 0% 100%)",
        zIndex: 2,
        boxShadow: "10px 0 50px rgba(0,0,0,0.5)",
        transform: `translateX(${sidebarX}%)`
      }}>
        {/* Texture */}
        <AbsoluteFill style={{
          background: `linear-gradient(135deg, ${theme.primary}22 0%, transparent 100%)`
        }} />

        {/* Moving Gloss/Sheen */}
        <AbsoluteFill style={{
          background: `linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.1) 50%, transparent 60%)`,
          backgroundSize: "200% 100%",
          backgroundPosition: `${sheenPos}% 0`,
        }} />
      </AbsoluteFill>

      {/* 3. CONTENT LAYER */}
      <AbsoluteFill
        style={{
          zIndex: 3,
          width: "42%",
        }}
      >
        {/* A. RANK BADGE */}
        {item.rank && (
          <div style={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            zIndex: 10, pointerEvents: 'none'
          }}>
            {/* REMOVED the conflicting transform: scale() wrapper here */}
            <div>
              {/* Pass the coordinated delay directly down to the child */}
              <RankBadge rank={item.rank} theme={theme} delay={audioOffset + 5} />
            </div>
          </div>
        )}

        {/* B. TEXT CONTAINER */}
        <div style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          padding: '60px 100px 60px 60px',
          paddingTop: '360px'
        }}>
          {/* METRIC */}
          {item.metric && (
            <div
              style={{
                transform: `translateY(${(1 - metricEnter) * 50}px)`,
                opacity: metricEnter,
                marginBottom: 10,
                flexShrink: 0, // <--- STABILITY FIX: Prevents bouncing
                willChange: "transform, opacity",
              }}
            >
              <h2
                style={{
                  fontSize: 95, // <--- TUNING: Slightly reduced from 100
                  lineHeight: 0.9,
                  fontFamily: theme.fontDisplay || theme.fontMain,
                  fontWeight: 400,
                  color: "white",
                  margin: 0,
                  textShadow: `2px 2px 0px ${theme.primary}`,
                  letterSpacing: -2,
                }}
              >
                {item.metric.toUpperCase()}
              </h2>
            </div>
          )}

          {/* NAME */}
          <div
            style={{
              transform: `translateX(${(1 - nameEnter) * -50}px)`,
              opacity: nameEnter,
              flexShrink: 0, // <--- STABILITY FIX: Prevents bouncing
              willChange: "transform, opacity",
            }}
          >
            <h1
              style={{
                fontSize: 60, // <--- TUNING: Slightly reduced from 65
                fontFamily: theme.fontMain,
                fontWeight: 700,
                color: "rgba(255,255,255,0.9)",
                textTransform: "uppercase",
                letterSpacing: 2,
                margin: "0 0 25px 0",
                borderLeft: `8px solid ${theme.secondary}`,
                paddingLeft: 30,
              }}
            >
              {item.name}
            </h1>
          </div>

          {/* HOOK / KARAOKE */}
          {(item.hook || (item.captions && item.captions.length > 0)) && (
            <div
              style={{
                transform: `translateY(${(1 - hookEnter) * 40}px)`,
                opacity: hookEnter,
                marginTop: 20,
                marginBottom: 40,
                display: "flex",
                // --- THE ARCHITECTURAL FIX: Layout Locking ---
                alignItems: "flex-start", // Anchor to the top so growth pushes downwards, not outwards.
                height: 180,              // Hard reservation of space. Replaces flexible minHeight.
                flexShrink: 0,            // Prevents flexbox from squishing this container.
                willChange: "transform, opacity",
              }}
            >
              {item.captions && item.captions.length > 0 ? (
                <div style={{
                  fontSize: 46,
                  fontFamily: theme.fontMain
                }}>
                  <KaraokeCaptions
                    captions={item.captions}
                    theme={theme}
                    delay={audioOffset}
                  />
                </div>
              ) : (
                <p
                  style={{
                    fontSize: 34,
                    fontFamily: theme.fontMain,
                    fontWeight: 500,
                    color: "#fff",
                    margin: 0,
                    lineHeight: 1.3,
                  }}
                >
                  {item.hook}
                </p>
              )}
            </div>
          )}


        </div>
      </AbsoluteFill>

      {/* BROADCAST BUG (WATERMARK) */}
      <div
        style={{
          position: "absolute",
          top: 50,
          right: 60,
          zIndex: 9, // <--- Sits above the background layer, below the progress bar
          opacity: watermarkOpacity,
          willChange: "opacity", // <--- GPU lock for the fade
        }}
      >
        <Img
          src="https://firebasestorage.googleapis.com/v0/b/freelancer-ideas-portal.firebasestorage.app/o/adaptation_engine%2Fbrand_assets%2Floveinc.png?alt=media&token=22866cd2-1b3b-4c74-9e24-762e6cfd2c33"
          style={{
            height: 125, // <--- Intentionally small and unobtrusive
            width: "auto",
            filter: "drop-shadow(0px 4px 15px rgba(0,0,0,0.8))", // <--- Crucial: Ensures visibility even if the background photo is pure white
          }}
        />
      </div>

      {/* 5. BRAND LOCKUP (Relocated to Bottom Right) */}
      <div
        style={{
          position: "absolute",
          bottom: 50, // <--- Nestles perfectly above the 12px progress bar
          right: 60,  // <--- Aligns vertically with the top-right watermark bug
          zIndex: 9,
          opacity: watermarkOpacity, // <--- Recycles our elegant 30-frame fade
          willChange: "opacity",
        }}
      >
        <h3
          style={{
            color: "white",
            fontFamily: theme.fontMain,
            fontWeight: 700, // <--- Bolder weight to ensure it punches through background noise
            letterSpacing: 4,
            margin: 0,
            fontSize: 26,
            textShadow: "0px 4px 15px rgba(0,0,0,0.9)", // <--- THE ARMOR: Heavy shadow ensures legibility on bright/white photos
          }}
        >
          LOVE{brand ? brand.toUpperCase() : "INC"}
        </h3>
      </div>

      {/* --- BEGIN SPRINT 1 INJECTION: COMPLIANCE WATERMARK --- */}
      {item.slide_image_citation_string && (
        <div
          style={{
            position: "absolute",
            bottom: 30, // Sits strictly beneath the Brand Lockup, avoiding the 12px progress bar
            right: 65,
            zIndex: 11, // Above the progress bar
            color: "rgba(255, 255, 255, 0.55)", // Subdued to avoid distracting from the narrative
            fontSize: 10,
            fontFamily: theme.fontMain,
            textTransform: "uppercase",
            letterSpacing: 1,
            textShadow: "1px 1px 4px rgba(0,0,0,0.9)", // Ensures readability on white/bright backgrounds
          }}
        >
          {item.slide_image_citation_string}
        </div>
      )}
      {/* --- END SPRINT 1 INJECTION --- */}

      {/* PROGRESS BAR */}
      <div
        style={{
          position: "absolute", bottom: 0, left: 0, height: 12, width: "100%",
          background: "rgba(255,255,255,0.2)", zIndex: 10
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${(frame / duration) * 100}%`,
            background: theme.primary,
            boxShadow: `0 0 20px ${theme.primary}`,
            position: "relative"
          }}
        >
          {/* Leading Edge */}
          <div style={{
            position: "absolute", right: 0, top: 0, bottom: 0, width: 4,
            background: "white", boxShadow: "0 0 15px white"
          }} />
        </div>
      </div>
    </AbsoluteFill>
  );
};